// Typed client for the ASSET REGISTRY — equipment a venue offers beside its
// courts (docs/domains/ASSET_BOOKING.md). On the `http` backend these are the
// `/assets` commands; on the memory backend (tests, no API) the registry is a
// blob under `assets_v1`, so the booking screens work the same way off-line as
// the court config does. There is no whole-collection write on the API — every
// change is one command naming one asset.
import { Storage } from '../storage';
import { apiFetch } from './http';

const KEY = 'assets_v1';

/** The categories the server accepts. Codes, never translated. */
export const ASSET_KINDS = ['ball_machine', 'racket', 'camera', 'other'] as const;
export type AssetKind = (typeof ASSET_KINDS)[number];

/** An asset as the API returns it — and as the screens hold it. */
export interface Asset {
  id: string;
  /** The venue it is kept at: the organization whose calendar shows it. */
  venueId: string;
  ownerTenantId: string;
  /** Whether THIS organization owns it (may edit) / keeps it (may book). */
  ownedHere: boolean;
  atThisVenue: boolean;
  venueName: string | null;
  ownerName: string | null;
  name: string;
  kind: AssetKind | string;
  description: string | null;
  active: boolean;
  sortOrder: number;
  /** Price zones in the booking engine's zone shape; null = default price only. */
  priceZones: any[] | null;
  defaultPricePerHour: number;
  /** The smallest gap between two bookings of it; 0 = any. */
  bufferMin: number;
  createdAt: number;
  updatedAt: number;
}

/** What a client may set. The venue travels only on create, as `venueId`. */
export interface AssetDraft {
  name: string;
  kind?: AssetKind | string;
  description?: string | null;
  active?: boolean;
  sortOrder?: number;
  priceZones?: any[] | null;
  defaultPricePerHour?: number;
  bufferMin?: number;
  /** Create only: another centre this organization is linked to. Absent = here. */
  venueId?: string;
}

export const assetsAreServerAuthoritative = () => Storage.backend === 'http';

async function readLocal(): Promise<Asset[]> {
  const r = await Storage.get(KEY);
  return r?.value ? (JSON.parse(r.value) as Asset[]) : [];
}

async function writeLocal(list: Asset[]): Promise<void> {
  await Storage.set(KEY, JSON.stringify(list));
}

/** A refusal the server sent, carried with its code so the screen can translate it. */
export class AssetCommandError extends Error {
  readonly code: string;
  readonly status: number;
  readonly detail: Record<string, unknown> | null;
  constructor(code: string, status: number, detail: Record<string, unknown> | null = null) {
    super(code);
    this.name = 'AssetCommandError';
    this.code = code;
    this.status = status;
    this.detail = detail;
  }
}

async function refusalFrom(res: Response): Promise<AssetCommandError> {
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  const code = typeof body?.error === 'string' ? body.error : `http_${res.status}`;
  return new AssetCommandError(code, res.status, body);
}

export async function fetchAssets(): Promise<Asset[]> {
  if (assetsAreServerAuthoritative()) {
    const res = await apiFetch('/assets');
    if (!res.ok) return [];
    const body = (await res.json()) as { assets?: Asset[] };
    return Array.isArray(body.assets) ? body.assets : [];
  }
  return readLocal();
}

/** The memory backend's stand-in for the server's row: this organization owns and keeps it. */
function localAsset(draft: AssetDraft, id: string): Asset {
  const now = Date.now();
  return {
    id,
    venueId: 'local',
    ownerTenantId: 'local',
    ownedHere: true,
    atThisVenue: true,
    venueName: null,
    ownerName: null,
    name: draft.name.trim(),
    kind: draft.kind ?? 'other',
    description: draft.description ?? null,
    active: draft.active !== false,
    sortOrder: draft.sortOrder ?? 0,
    priceZones: Array.isArray(draft.priceZones) ? draft.priceZones : null,
    defaultPricePerHour: draft.defaultPricePerHour ?? 0,
    bufferMin: draft.bufferMin ?? 0,
    createdAt: now,
    updatedAt: now,
  };
}

export async function createAsset(draft: AssetDraft): Promise<Asset> {
  if (assetsAreServerAuthoritative()) {
    const res = await apiFetch('/assets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(draft),
    });
    if (!res.ok) throw await refusalFrom(res);
    return (await res.json()) as Asset;
  }
  const list = await readLocal();
  const created = localAsset(
    draft,
    `asset_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  );
  await writeLocal([...list, created]);
  return created;
}

export async function patchAsset(id: string, patch: Partial<AssetDraft>): Promise<Asset> {
  if (assetsAreServerAuthoritative()) {
    const res = await apiFetch('/assets/' + encodeURIComponent(id), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw await refusalFrom(res);
    return (await res.json()) as Asset;
  }
  const list = await readLocal();
  const idx = list.findIndex((a) => a.id === id);
  if (idx < 0) throw new AssetCommandError('not_found', 404);
  const { venueId: _venue, ...rest } = patch;
  const next = { ...list[idx], ...rest, updatedAt: Date.now() } as Asset;
  const out = [...list];
  out[idx] = next;
  await writeLocal(out);
  return next;
}

export async function deleteAsset(id: string): Promise<void> {
  if (assetsAreServerAuthoritative()) {
    const res = await apiFetch('/assets/' + encodeURIComponent(id), { method: 'DELETE' });
    if (!res.ok) throw await refusalFrom(res);
    return;
  }
  const list = await readLocal();
  await writeLocal(list.filter((a) => a.id !== id));
}
